import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useCallback, useLayoutEffect } from "react";

import { ItemInstance, TreeInstance } from "@headless-tree/core";
import { Virtualizer } from "@tanstack/react-virtual";

import { planReveal } from "./planReveal";
import { TreeNodeData } from "./registry";
import { TreeIndex, TreeItem } from "./treeIndex";
import { useTreeExpansion } from "./useTreeExpansion";

/**
 * Expand an item's ancestors and scroll it into view.
 *
 * Queued rather than applied directly: the target usually isn't loaded yet, and panes mount
 * lazily. Moves the cursor only — never activates, never writes selection, and `focus` is opt-in
 * so a mutation-triggered reveal cannot pull focus out of the editor. See §3.1 of
 * docs/model-tool-sidebar-tree.md.
 */
export type RevealRequest = {
  targetId: string;
  /** Also expand the target. Only meaningful for a folder. */
  expandTarget?: boolean;
  /** Move real DOM focus. User-initiated callers only. */
  focus?: boolean;
  align?: "start" | "center" | "end" | "auto";
  /** Monotonic, so an identical repeat request still fires. */
  nonce: number;
};

const pendingRevealFamily = atomFamily((_treeId: string) =>
  atom<RevealRequest | null>(null),
);

/** Requests a reveal on the tree registered under `treeId`. */
export function useTreeRevealRequester(treeId: string) {
  const setPending = useSetAtom(pendingRevealFamily(treeId));

  return useCallback(
    (request: Omit<RevealRequest, "nonce">) => {
      // Functional form: reading `nonce` from a render closure would both lose rapid
      // successive requests and churn this callback's identity on every reveal.
      setPending((prev) => ({ ...request, nonce: (prev?.nonce ?? 0) + 1 }));
    },
    [setPending],
  );
}

/** Clears any queued request. */
export function useClearTreeReveal(treeId: string) {
  const setPending = useSetAtom(pendingRevealFamily(treeId));
  return useCallback(() => setPending(null), [setPending]);
}

/** Call from the component owning the tree's expansion state, so the row exists before scroll. */
export function useTreeRevealExpansion<D extends TreeNodeData>(
  treeId: string,
  index: TreeIndex<D>,
) {
  const pending = useAtomValue(pendingRevealFamily(treeId));
  const { expandedItems, setExpandedItems } = useTreeExpansion(treeId);

  useLayoutEffect(() => {
    if (pending == null) return;
    const plan = planReveal(index, pending.targetId, expandedItems, {
      expandTarget: pending.expandTarget,
    });
    // `null` means not loaded yet; stay queued.
    if (plan == null || !plan.didExpand) return;
    setExpandedItems(plan.expandedItems);
  }, [pending, index, expandedItems, setExpandedItems]);
}

/**
 * Scrolls to and cursors the queued target once it is in the visible list. Keyed on `items`
 * rather than a rAF, since the row only appears after the expansion above is applied.
 */
export function useTreeRevealTarget<D extends TreeNodeData>(args: {
  treeId?: string;
  tree: TreeInstance<TreeItem<D>>;
  items: ItemInstance<TreeItem<D>>[];
  virtualizer?: Virtualizer<HTMLDivElement, Element>;
}) {
  const { treeId, tree, items, virtualizer } = args;
  // Trees without a treeId opt out of reveal.
  const [pending, setPending] = useAtom(pendingRevealFamily(treeId ?? ""));

  useLayoutEffect(() => {
    if (treeId == null || pending == null) return;

    const visibleIndex = items.findIndex(
      (item) => item.getId() === pending.targetId,
    );
    if (visibleIndex === -1) return; // stay queued

    virtualizer?.scrollToIndex(visibleIndex, {
      align: pending.align ?? "center",
    });

    const item = items[visibleIndex];
    item.setFocused();
    if (pending.focus === true) {
      tree.updateDomFocus();
    } else if (virtualizer == null) {
      item.scrollTo({ block: "nearest", inline: "nearest" });
    }

    setPending(null);
  }, [treeId, pending, items, virtualizer, tree, setPending]);
}
